import { apiFetch } from './api-client';

export type WindowDays = 7 | 30 | 90;

export interface AnalyticsSummary {
  window: { start: string; end: string; days: number };
  /** True when the RewardNFT verification mirror's last sync is >24h old (or
   *  never ran) — during a chain outage, verifyFromMirror refuses to
   *  authorise redemptions off a mirror in this state. */
  rewardMirrorStale: boolean;
  shop: { tier: 'free' | 'founding' | 'pro'; founding_number: number | null; timezone: string; at_risk_days: number };
  stamps: {
    total: number;
    delta_pct: number | null;
    /** `count` is stamps issued that day, `redeemed` free coffees handed over. */
    daily: { date: string; count: number; redeemed: number }[];
    today_live: number;
  };
  regulars: { address: string; display_name: string | null; last_visit_at: string }[];
  at_risk: { address: string; display_name: string | null; last_visit_at: string }[];
  top_customers: TopCustomerRow[];
  roi: {
    stamps: number; redemptions: number; comps_sent: number; comps_redeemed: number; est_revenue: number;
    /** % of comped customers back within 14 days — null until a comp has had a full 14 days to convert. */
    comp_return_rate: number | null;
  };
  /** % of first-visit customers returning within 2 weeks — null when the cohort is below 5 (backlog #15). */
  retention: { rate: number | null; cohortSize: number };
  peak_hours: number[];
}

export interface TopCustomerRow {
  customer_key: string; first_name: string; stamps: number;
  /** Cloudinary URL from the avatar-chip upload; null when never set. */
  avatar_url: string | null;
  last_visit: string; streak_weeks: number;
}
export interface CustomerDetail {
  first_name: string; total_stamps: number; free_coffees: number;
  comps_received: number; comps_used: number; est_spend_cents: number;
  avg_price_cents: number; first_visit: string | null; last_visit: string | null;
  streak_weeks: number; badges: string[];
}

export async function fetchAnalyticsSummary(
  shopId: string,
  windowDays: WindowDays = 30,
): Promise<AnalyticsSummary> {
  return apiFetch<AnalyticsSummary>('/api/gamification', {
    action: 'analytics-summary',
    shopId,
    windowDays,
  });
}

export async function fetchCustomerDetail(shopId: string, customerKey: string): Promise<CustomerDetail> {
  return apiFetch<CustomerDetail>('/api/gamification', {
    action: 'customer-detail', shopId, customerKey,
  });
}

export interface RewardMirrorStatus {
  rewardMirrorStale: boolean;
}

/**
 * Cheap, tier-agnostic companion to fetchAnalyticsSummary's rewardMirrorStale
 * field — for callers (like the operational dashboard tab) that need only
 * this one boolean and shouldn't pay for the tier-gated analytics payload's
 * ~5000-row scan just to get it (fix round 1, Finding 3).
 */
export async function fetchRewardMirrorStatus(): Promise<RewardMirrorStatus> {
  return apiFetch<RewardMirrorStatus>('/api/gamification', {
    action: 'reward-mirror-status',
  });
}

export interface ShopGoalStatus {
  goal: { enabled: boolean; target: number; label: string } | null;
  served: number;
  weekStart?: string;
}

/**
 * The shop's weekly goal and how far along it is.
 *
 * Separate from fetchAnalyticsSummary on purpose: that payload is gated to
 * founding/pro shops and does a ~5000-row scan, and the customers who see this
 * card on HOME are not shop owners at all.
 */
export async function fetchShopGoal(shopId: string): Promise<ShopGoalStatus> {
  return apiFetch<ShopGoalStatus>('/api/gamification', { action: 'shop-goal', shopId });
}
